import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Tab, Tabs, Row, Col, Badge, Button, Modal, Form, Alert, Table } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import api from '../lib/api';
import { useAuth } from '../lib/auth.jsx';
import PageHeader from '../components/PageHeader.jsx';
import AttachmentList from '../components/AttachmentList.jsx';
import { fmtMoney, fmtDate, fmtDateTime, STATUS_BADGE, REF_KIND_LABEL, PAYMENT_METHOD_LABEL } from '../lib/format.js';

export default function CustomerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const qc = useQueryClient();

  const detail = useQuery({
    queryKey: ['customer', id],
    queryFn: () => api.get(`/customers/${id}`).then(r => r.data)
  });

  const [refModal, setRefModal] = useState(false);
  const [refDraft, setRefDraft] = useState({ kind: 'saas_org', deployment: '', external_id: '', label: '' });
  const [refError, setRefError] = useState(null);

  const addRef = useMutation({
    mutationFn: () => api.post(`/customers/${id}/external-refs`, refDraft).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customer', id] });
      setRefModal(false);
      setRefDraft({ kind: 'saas_org', deployment: '', external_id: '', label: '' });
    },
    onError: (e) => setRefError(e.response?.data?.message || JSON.stringify(e.response?.data?.details || e.response?.data))
  });
  const removeRef = useMutation({
    mutationFn: (refId) => api.delete(`/customers/${id}/external-refs/${refId}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customer', id] })
  });
  // #43: setPrimary markiert einen ref als Token-Identity. Backend syncrt
  // customers.external_id automatisch.
  const setPrimary = useMutation({
    mutationFn: (refId) => api.post(`/customers/${id}/external-refs/${refId}/set-primary`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customer', id] }),
    onError: (e) => setRefError(e.response?.data?.message || JSON.stringify(e.response?.data?.details || e.response?.data))
  });

  const newInvoice = useMutation({
    mutationFn: () => api.post('/invoices', { customer_id: id }).then(r => r.data),
    onSuccess: (data) => navigate(`/invoices/${data.invoice.id}`)
  });

  // Addon-API-Key Reset (Operator-Action): Plaintext kommt einmalig zurueck,
  // wird in einem Modal mit Copy-to-Clipboard angezeigt und nicht persistiert.
  const [addonKeyResult, setAddonKeyResult] = useState(null);
  const [addonKeyError, setAddonKeyError] = useState(null);
  const [copied, setCopied] = useState(false);
  const resetAddonKey = useMutation({
    mutationFn: () => api.post(`/customers/${id}/reset-addon-key`).then(r => r.data),
    onSuccess: (data) => { setAddonKeyResult(data); setAddonKeyError(null); setCopied(false); },
    onError: (e) => setAddonKeyError(
      e.response?.data?.message
      || e.response?.data?.code
      || e.message
    )
  });

  if (detail.isLoading) return <div className="text-muted">Lade…</div>;
  if (detail.error) return <Alert variant="danger">Kunde nicht gefunden.</Alert>;

  const c = detail.data.customer;
  const refs = detail.data.external_refs || [];
  const licenses = detail.data.licenses || [];
  const invoices = detail.data.invoices || [];

  const totalOpen = invoices.filter(i => ['issued','overdue'].includes(i.status))
    .reduce((s, i) => s + (i.open_cents || 0), 0);
  const totalOverdue = invoices.filter(i => i.status === 'overdue')
    .reduce((s, i) => s + (i.open_cents || 0), 0);
  const totalPaid = invoices.reduce((s, i) => s + (i.paid_cents || 0), 0);
  const currency = invoices[0]?.currency || 'EUR';

  return (
    <>
      <PageHeader
        title={c.name}
        subtitle={<>Kundennummer: <code>{c.customer_number}</code></>}
        actions={
          <>
            <Button variant="outline-secondary" onClick={() => navigate('/customers')}>
              <Icon icon="tabler:arrow-left" className="me-1" />Zurück
            </Button>
            {auth.can('invoices', 'create') && (
              <Button onClick={() => newInvoice.mutate()}>
                <Icon icon="tabler:file-invoice" className="me-1" />Neue Rechnung
              </Button>
            )}
          </>
        }
      />

      {/* KPI-Reihe */}
      <Row className="g-3 mb-3">
        <Col md={3}>
          <Card className="card-flat h-100"><Card.Body>
            <div className="text-muted small text-uppercase">Lizenzen aktiv</div>
            <div className="fs-4 fw-semibold">{licenses.filter(l => !l.revoked_at).length}</div>
          </Card.Body></Card>
        </Col>
        <Col md={3}>
          <Card className="card-flat h-100"><Card.Body>
            <div className="text-muted small text-uppercase">Offen</div>
            <div className="fs-4 fw-semibold">{fmtMoney(totalOpen, currency)}</div>
          </Card.Body></Card>
        </Col>
        <Col md={3}>
          <Card className={`card-flat h-100 ${totalOverdue > 0 ? 'border-danger' : ''}`}><Card.Body>
            <div className="text-muted small text-uppercase">Überfällig</div>
            <div className={`fs-4 fw-semibold ${totalOverdue > 0 ? 'text-danger' : ''}`}>{fmtMoney(totalOverdue, currency)}</div>
          </Card.Body></Card>
        </Col>
        <Col md={3}>
          <Card className="card-flat h-100"><Card.Body>
            <div className="text-muted small text-uppercase">Bisher bezahlt</div>
            <div className="fs-4 fw-semibold">{fmtMoney(totalPaid, currency)}</div>
          </Card.Body></Card>
        </Col>
      </Row>

      <Tabs defaultActiveKey="master" className="mb-3" mountOnEnter>
        {/* ─── Stammdaten ─── */}
        <Tab eventKey="master" title="Stammdaten">
          <Card className="card-flat"><Card.Body>
            <Row>
              <Col md={6}>
                <h6 className="text-uppercase text-muted small mb-2">Firma</h6>
                <div className="mb-1"><strong>{c.name}</strong></div>
                {c.contact_person && <div className="text-muted">Ansprechpartner: {c.contact_person}</div>}
                {c.contact_email && <div><Icon icon="tabler:mail" className="me-1 text-muted" />{c.contact_email}</div>}
                {c.contact_phone && <div><Icon icon="tabler:phone" className="me-1 text-muted" />{c.contact_phone}</div>}
                {c.website && <div><Icon icon="tabler:world" className="me-1 text-muted" />{c.website}</div>}

                <h6 className="text-uppercase text-muted small mb-2 mt-4">Anschrift</h6>
                {(c.address_street || c.address_city) ? (
                  <>
                    {c.address_street && <div>{c.address_street}</div>}
                    <div>{[c.address_zip, c.address_city].filter(Boolean).join(' ')}</div>
                    {c.address_country && <div>{c.address_country}</div>}
                  </>
                ) : <div className="text-muted">—</div>}
              </Col>
              <Col md={6}>
                <h6 className="text-uppercase text-muted small mb-2">Abrechnung</h6>
                <Row className="g-2">
                  <Col sm={6}><div className="text-muted small">USt-ID</div><div>{c.vat_id || '—'}</div></Col>
                  <Col sm={6}><div className="text-muted small">Referenz</div><div>{c.billing_reference || '—'}</div></Col>
                  <Col sm={6}><div className="text-muted small">Erstellt</div><div>{fmtDateTime(c.created_at)}</div></Col>
                  <Col sm={6}><div className="text-muted small">Status</div>
                    <div>{c.archived_at
                      ? <Badge bg="" className="badge-soft-secondary">Archiviert</Badge>
                      : <Badge bg="" className="badge-soft-success">Aktiv</Badge>}
                    </div>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Card.Body></Card>
        </Tab>

        {/* ─── Customer-API-Key ─── */}
        <Tab eventKey="addon-api" title="Customer-API">
          <Card className="card-flat"><Card.Body>
            <h6 className="text-uppercase text-muted small mb-2">Customer-API-Key</h6>
            <p className="text-muted small mb-3">
              Der API-Key fuer den Customer-API-Zugriff. Klick auf "Zuruecksetzen" widerruft den
              alten Key und stellt einen neuen aus. Der Plaintext wird nur einmal angezeigt — danach ist er nicht
              mehr abrufbar.
            </p>
            {addonKeyError && (
              <Alert variant="danger" dismissible onClose={() => setAddonKeyError(null)} className="small">
                {addonKeyError}
              </Alert>
            )}
            <Button
              variant="outline-warning"
              disabled={!auth.can('customers', 'update') || resetAddonKey.isLoading}
              onClick={() => {
                if (!confirm('Bestehenden API-Key widerrufen und neuen ausstellen? Diese Aktion ist nicht umkehrbar.')) return;
                resetAddonKey.mutate();
              }}>
              <Icon icon="tabler:key" className="me-1" />
              {resetAddonKey.isLoading ? 'Setze zurueck…' : 'Customer-API-Key zuruecksetzen'}
            </Button>
          </Card.Body></Card>
        </Tab>

        {/* ─── Lizenzen ─── */}
        <Tab eventKey="licenses" title={<>Lizenzen <Badge bg="" className="badge-soft-secondary">{licenses.length}</Badge></>}>
          <Card className="card-flat"><Card.Body className="p-0">
            {licenses.length === 0 ? (
              <div className="empty-state">
                <Icon icon="tabler:certificate-off" className="empty-icon" />
                <div>Noch keine Lizenzen für diesen Kunden.</div>
              </div>
            ) : (
              <Table hover className="mb-0 align-middle">
                <thead>
                  <tr><th>Name</th><th>Status</th><th>Gültig bis</th><th>Erstellt</th><th></th></tr>
                </thead>
                <tbody>
                  {licenses.map(l => (
                    <tr key={l.id}>
                      <td><Link to={`/licenses/${l.id}`} className="text-decoration-none fw-semibold">{l.name}</Link></td>
                      <td>{l.revoked_at
                        ? <Badge bg="" className="badge-soft-danger">Widerrufen</Badge>
                        : <Badge bg="" className="badge-soft-success">Aktiv</Badge>}</td>
                      <td>{l.valid_until ? fmtDate(l.valid_until) : <span className="text-muted">∞</span>}</td>
                      <td className="text-muted small">{fmtDate(l.created_at)}</td>
                      <td className="text-end pe-3">
                        <Button as={Link} to={`/licenses/${l.id}`} size="sm" variant="outline-secondary">
                          <Icon icon="tabler:eye" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body></Card>
        </Tab>

        {/* ─── Rechnungen ─── */}
        <Tab eventKey="invoices" title={<>Rechnungen <Badge bg="" className="badge-soft-secondary">{invoices.length}</Badge></>}>
          <Card className="card-flat"><Card.Body className="p-0">
            {invoices.length === 0 ? (
              <div className="empty-state">
                <Icon icon="tabler:file-invoice" className="empty-icon" />
                <div>Noch keine Rechnungen.</div>
                {auth.can('invoices', 'create') && (
                  <Button className="mt-2" onClick={() => newInvoice.mutate()}>
                    <Icon icon="tabler:plus" className="me-1" />Erste Rechnung anlegen
                  </Button>
                )}
              </div>
            ) : (
              <Table hover className="mb-0 align-middle">
                <thead>
                  <tr><th>Nr.</th><th>Status</th><th>Datum</th><th>Fällig</th><th>Mahnstufe</th><th className="text-end">Brutto</th><th className="text-end">Offen</th><th></th></tr>
                </thead>
                <tbody>
                  {invoices.map(inv => {
                    const sb = STATUS_BADGE[inv.status] || { variant: 'secondary', label: inv.status };
                    return (
                      <tr key={inv.id}>
                        <td>{inv.invoice_number
                          ? <Link to={`/invoices/${inv.id}`} className="text-decoration-none">{inv.invoice_number}</Link>
                          : <Link to={`/invoices/${inv.id}`} className="text-muted text-decoration-none">Entwurf</Link>}</td>
                        <td><Badge bg="" className={`badge-soft-${sb.variant}`}>{sb.label}</Badge></td>
                        <td className="small">{inv.issue_date ? fmtDate(inv.issue_date) : '—'}</td>
                        <td className="small">{inv.due_date ? fmtDate(inv.due_date) : '—'}</td>
                        <td>{inv.current_dunning_level > 0
                          ? <Badge bg="" className="badge-soft-warning">Stufe {inv.current_dunning_level}</Badge>
                          : <span className="text-muted small">—</span>}</td>
                        <td className="text-end">{fmtMoney(inv.total_cents, inv.currency)}</td>
                        <td className="text-end">{inv.status === 'paid' ? '—' : fmtMoney(inv.open_cents, inv.currency)}</td>
                        <td className="text-end pe-3">
                          <Button as={Link} to={`/invoices/${inv.id}`} size="sm" variant="outline-secondary">
                            <Icon icon="tabler:eye" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </Card.Body></Card>
        </Tab>

        {/* ─── Anhänge ─── */}
        <Tab eventKey="attachments" title={<>Anhänge <Badge bg="" className="badge-soft-secondary">{(detail.data.attachments || []).length}</Badge></>}>
          <Card className="card-flat"><Card.Body>
            <div className="text-muted small mb-3">
              Verträge, Korrespondenz, Auftragsbestätigungen, sonstige Dokumente.
            </div>
            <AttachmentList parentType="customer" parentId={c.id}
              items={detail.data.attachments || []}
              onChange={() => qc.invalidateQueries({ queryKey: ['customer', id] })} />
          </Card.Body></Card>
        </Tab>

        {/* ─── Externe Verknüpfungen ─── */}
        <Tab eventKey="refs" title={<>Externe Verknüpfungen <Badge bg="" className="badge-soft-secondary">{refs.length}</Badge></>}>
          <Card className="card-flat"><Card.Body>
            {refError && (
              <Alert variant="danger" dismissible onClose={() => setRefError(null)} className="small">{refError}</Alert>
            )}
            {refs.length > 0 && !refs.some(r => r.is_primary) && (
              <Alert variant="warning" className="small">
                Bis hier eine Verknüpfung mit <strong>Primary</strong> markiert ist, hat dieser Customer keine
                Token-Identity und kann keine Addon-API-Lizenz nutzen.
              </Alert>
            )}
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div className="text-muted small">
                Ordnet diesen Kunden konkreten Deployments zu (SaaS-Org, Unit, On-Prem-Installation, Edge-Box).
                Eindeutig per <code>(kind, deployment, external_id)</code>. Der als <strong>Primary</strong>{' '}
                markierte Ref stellt die Token-Identity (<code>customer.external_id</code>).
              </div>
              {auth.can('external_refs', 'manage') && (
                <Button size="sm" onClick={() => { setRefModal(true); setRefError(null); }}>
                  <Icon icon="tabler:plus" className="me-1" />Hinzufügen
                </Button>
              )}
            </div>
            {refs.length === 0 ? (
              <div className="text-muted">Keine Verknüpfungen.</div>
            ) : (
              <Table hover className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th style={{ width: 70 }}>Primary</th>
                    <th>Typ</th><th>Deployment</th><th>External ID</th><th>Label</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {refs.map(r => (
                    <tr key={r.id}>
                      <td>
                        <Form.Check type="radio" name="primary-ref"
                          checked={!!r.is_primary}
                          disabled={!auth.can('customers', 'update') || setPrimary.isLoading}
                          onChange={() => { if (!r.is_primary) setPrimary.mutate(r.id); }}
                          title={r.is_primary ? 'Primary' : 'Als Primary markieren'} />
                      </td>
                      <td><Badge bg="" className="badge-soft-primary">{REF_KIND_LABEL[r.kind] || r.kind}</Badge></td>
                      <td><code className="small">{r.deployment}</code></td>
                      <td>{r.external_id ? <code className="small">{r.external_id}</code> : <span className="text-muted">—</span>}</td>
                      <td className="text-muted small">{r.label || '—'}</td>
                      <td className="text-end pe-3">
                        {auth.can('external_refs', 'manage') && (
                          <Button size="sm" variant="outline-danger"
                            onClick={() => confirm('Verknüpfung wirklich entfernen?') && removeRef.mutate(r.id)}>
                            <Icon icon="tabler:trash" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
            <div className="text-muted small mt-3">
              Token-Identity: {c.external_id
                ? <code>{c.external_id}</code>
                : <em>nicht gesetzt — kein Primary-Ref markiert.</em>}
            </div>
          </Card.Body></Card>
        </Tab>
      </Tabs>

      {/* Add ref modal */}
      <Modal show={refModal} onHide={() => setRefModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>Externe Verknüpfung</Modal.Title></Modal.Header>
        <Modal.Body>
          {refError && <Alert variant="danger" className="py-2 small">{refError}</Alert>}
          <Form.Group className="mb-3">
            <Form.Label>Typ</Form.Label>
            <Form.Select value={refDraft.kind} onChange={(e) => setRefDraft({ ...refDraft, kind: e.target.value })}>
              <option value="saas_org">SaaS-Organisation</option>
              <option value="saas_unit">SaaS-Einheit</option>
              <option value="on_premise">On-Premise-Installation</option>
              <option value="edge_box">Edge-Box</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Deployment *</Form.Label>
            <Form.Control required value={refDraft.deployment}
              onChange={(e) => setRefDraft({ ...refDraft, deployment: e.target.value })}
              placeholder="z.B. beta.uav-bos.de oder Install-UUID" />
            <Form.Text>Bei SaaS: Hostname. Bei On-Premise: UUID der Installation.</Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>External ID</Form.Label>
            <Form.Control value={refDraft.external_id}
              onChange={(e) => setRefDraft({ ...refDraft, external_id: e.target.value })}
              placeholder="z.B. org_id, unit_id, box_id" />
          </Form.Group>
          <Form.Group>
            <Form.Label>Label (optional)</Form.Label>
            <Form.Control value={refDraft.label}
              onChange={(e) => setRefDraft({ ...refDraft, label: e.target.value })}
              placeholder="z.B. Standort Berlin" />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setRefModal(false)}>Abbrechen</Button>
          <Button onClick={() => addRef.mutate()} disabled={!refDraft.deployment}>
            Verknüpfen
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Addon-API-Key Plaintext-Modal: zeigt den Key EINMALIG nach Reset an */}
      <Modal show={!!addonKeyResult} onHide={() => setAddonKeyResult(null)} centered backdrop="static">
        <Modal.Header closeButton><Modal.Title>Neuer Addon-API-Key</Modal.Title></Modal.Header>
        <Modal.Body>
          <Alert variant="warning" className="small">
            <Icon icon="tabler:alert-triangle" className="me-1" />
            Dieser Key wird nur einmal angezeigt. Bitte jetzt sicher uebertragen — er ist danach nicht mehr abrufbar.
          </Alert>
          <Form.Group className="mb-3">
            <Form.Label>API-Key (Plaintext)</Form.Label>
            <div className="d-flex gap-2">
              <Form.Control readOnly value={addonKeyResult?.plaintext || ''}
                style={{ fontFamily: 'monospace' }}
                onFocus={(e) => e.target.select()} />
              <Button variant="outline-secondary" onClick={() => {
                if (addonKeyResult?.plaintext) {
                  navigator.clipboard.writeText(addonKeyResult.plaintext);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }
              }}>
                <Icon icon={copied ? 'tabler:check' : 'tabler:copy'} className="me-1" />
                {copied ? 'Kopiert' : 'Kopieren'}
              </Button>
            </div>
          </Form.Group>
          {addonKeyResult?.meta && (
            <div className="small text-muted">
              {addonKeyResult.meta.label && <div>Label: <code>{addonKeyResult.meta.label}</code></div>}
              {addonKeyResult.meta.last4 && <div>Letzte 4 Zeichen: <code>{addonKeyResult.meta.last4}</code></div>}
              {addonKeyResult.meta.created_at && <div>Erstellt: {addonKeyResult.meta.created_at}</div>}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setAddonKeyResult(null)}>
            Schliessen
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
